import { Flex, Text } from "@chakra-ui/react";
import {
    OptionBase, chakraComponents
} from "chakra-react-select";
import { Control, Controller } from "react-hook-form";
import { countries, supported } from "src/data/Countries";
import CountryFlag from "../CountryFlag";
import { Field } from "@/components/ui/field";
import {
    Select as ReactSelect
  } from "chakra-react-select";


export interface CountryOption extends OptionBase {
    label: string;
    value: string;
    icon: React.ReactElement
}

const countryOptions = (desiredCountryCodes: Set<string>, language: string): CountryOption[] => {
    const displayNames = new Intl.DisplayNames([language], { type: 'region' });
    return Object.keys(countries)
        .filter((k) => desiredCountryCodes.has(k))
        .map((k) => ({
            label: displayNames.of(k) ?? countries[k],
            value: k,
            icon: <CountryFlag code={k} />
        }))
        .sort((a, b) => a.label.localeCompare(b.label, language));
};


const customItem = (item: CountryOption) => (
    <Flex align="center">
        {item.icon}
        <Text ml="10px">{item.label}</Text>
    </Flex>
);

const customOption = (props) => (
    <chakraComponents.Option label={props.data.name} {...props}>
        {customItem(props.data)}
    </chakraComponents.Option>
);

type CountrySelectControllerProps = {
    control: Control<any, any>,
    name: string,
    readOnly: boolean,
    countryCodes?: string[],
    label?: string,
    placeholder?: string,
    requiredMessage?: string,
    language?: string,
    onValueChange?: (value: string) => void,
    [key: string]: any
}

const CountrySelectController = ({
    control,
    name,
    readOnly,
    countryCodes,
    label = 'Land',
    placeholder = 'Land auswählen',
    requiredMessage = 'Land auswählen',
    language = 'en',
    onValueChange,
    ...props
}: CountrySelectControllerProps) => {
    const desiredCountryCodes = countryCodes === undefined
        ? supported
        : new Set((countryCodes ?? []).map((code) => code.toUpperCase()));
    const filteredOptions = countryOptions(desiredCountryCodes, language);

    return (
        <Controller control={control} name={name}
            rules={{ required: requiredMessage, validate: (value) => value.length > 0 }}
            render={({ field: { value, name, onChange, onBlur, ref }, fieldState: { error } }) => (
                <Field id={name} label={label} required invalid={!!error} errorText={error?.message} disabled={readOnly}>
                    <ReactSelect
                        isSearchable
                        required
                        name={name}
                        ref={ref}
                        onBlur={onBlur}
                        onChange={val => {
                            if (!val) return;
                            onValueChange?.(val.value);
                            onChange(val.value);
                        }}
                        value={filteredOptions.find(c => c.value === value)}
                        placeholder={placeholder}
                        options={filteredOptions}
                        components={{ Option: customOption }}
                        // show the land flag for the selected item
                        formatOptionLabel={(option: CountryOption) => customItem(option)}
                        getOptionLabel={(option) => option.label}
                        getOptionValue={(option) => option.value}
                        {...props}
                    />
                </Field>
            )} />
    );
}

export default CountrySelectController
